import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TableRestaurantOutlined as SalonIcon,
  RoomServiceOutlined as MeseroIcon,
  RestaurantOutlined as CocinaIcon,
  PointOfSaleOutlined as CajaIcon,
  PeopleAltOutlined as AnfitrionIcon,
  WifiRounded as WifiIcon,
  RefreshRounded as RefreshIcon,
  LogoutRounded as LogoutIcon,
  CloseRounded as CloseIcon,
} from "@mui/icons-material";
import api from "../api/api";

/**
 * Tavolata by IntegraNube
 * src/components/RestaurantNavbar.js
 *
 * Navbar principal de Tavolata.
 *
 * IMPORTANTE:
 * - Se mantiene FIXED porque RestaurantShell ya reserva el espacio superior.
 * - Esto evita el doble espacio que aparecía con position: sticky.
 * - Usa /tavolata-logo.png.
 * - Usa exclusivamente @mui/icons-material para la iconografía del navbar.
 * - Obtiene indicadores desde GET /restaurante/salon.
 * - Permite indicadores externos mediante props o CustomEvent.
 * - Incluye cierre de sesión con confirmación y fallback autónomo.
 *
 * Evento soportado:
 *
 * window.dispatchEvent(
 *   new CustomEvent("tavolata:indicators", {
 *     detail: {
 *       cocina: { count: 4, label: "4 listas", tone: "success" },
 *       caja: { count: 2, label: "2 cuentas", tone: "danger" },
 *     },
 *   })
 * );
 */

const REFRESH_MS = 12000;
const LOGO_SRC = "/tavolata-logo.png";

const COLORS = {
  ink: "#17211c",
  muted: "#68766e",
  line: "#e5e9e6",
  surface: "#ffffff",
  surfaceSoft: "#f7f9f8",

  brand: "#e68d2f",
  brandDark: "#ba6e1d",
  brandSoft: "#fff4e7",

  green: "#168a52",
  greenSoft: "#e8f8ef",

  red: "#d83d43",
  redSoft: "#fff0f1",

  amber: "#bd7910",
  amberSoft: "#fff6dd",

  blue: "#2f78c7",
  blueSoft: "#edf5ff",

  purple: "#7d4fc6",
  purpleSoft: "#f5efff",
};

const STATUS_ALIASES = {
  DISPONIBLE: "DISPONIBLE",
  LIBRE: "DISPONIBLE",

  RESERVADA: "RESERVADA",
  RESERVADO: "RESERVADA",

  ESPERANDO: "ESPERANDO_PEDIDO",
  ESPERANDO_PEDIDO: "ESPERANDO_PEDIDO",
  PENDIENTE_PEDIDO: "ESPERANDO_PEDIDO",

  ABIERTA: "ABIERTA",
  ABIERTO: "ABIERTA",
  PEDIDO_ABIERTO: "ABIERTA",

  EN_COCINA: "EN_COCINA",
  COCINA: "EN_COCINA",
  PREPARANDO: "EN_COCINA",
  EN_PREPARACION: "EN_COCINA",

  LISTA: "LISTA",
  LISTO: "LISTA",
  PEDIDO_LISTO: "LISTA",
  LISTO_PARA_SERVIR: "LISTA",
  LISTA_PARA_SERVIR: "LISTA",

  OCUPADA: "OCUPADA",
  OCUPADO: "OCUPADA",

  CUENTA: "CUENTA_SOLICITADA",
  CUENTA_SOLICITADA: "CUENTA_SOLICITADA",
  SOLICITO_CUENTA: "CUENTA_SOLICITADA",

  FUERA_SERVICIO: "FUERA_SERVICIO",
  BLOQUEADA: "FUERA_SERVICIO",
  BLOQUEADO: "FUERA_SERVICIO",
};

const NAV_ICONS = {
  salon: SalonIcon,
  mesero: MeseroIcon,
  cocina: CocinaIcon,
  caja: CajaIcon,
  anfitrion: AnfitrionIcon,
};

function first(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function normalizeStatus(value) {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (!normalized) return "";
  if (STATUS_ALIASES[normalized]) return STATUS_ALIASES[normalized];

  if (normalized.includes("CUENTA")) return "CUENTA_SOLICITADA";
  if (normalized.includes("LIST")) return "LISTA";
  if (normalized.includes("COCINA") || normalized.includes("PREPAR")) return "EN_COCINA";
  if (normalized.includes("RESERV")) return "RESERVADA";
  if (normalized.includes("DISPON") || normalized.includes("LIBRE")) return "DISPONIBLE";
  if (normalized.includes("ESPER")) return "ESPERANDO_PEDIDO";
  if (normalized.includes("ABIER")) return "ABIERTA";
  if (normalized.includes("OCUP")) return "OCUPADA";
  if (normalized.includes("FUERA") || normalized.includes("BLOQUE")) return "FUERA_SERVICIO";

  return normalized;
}

function extractArray(payload, keys = []) {
  if (Array.isArray(payload)) return payload;

  const sources = [
    payload,
    payload?.data,
    payload?.result,
    payload?.salon,
    payload?.data?.salon,
  ];

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      if (Array.isArray(source[key])) return source[key];
    }
  }

  return [];
}

function extractTables(payload) {
  let tables = extractArray(payload, ["mesas", "tables"]);
  const zones = extractArray(payload, ["zonas", "zones", "sectores"]);

  if (!tables.length && zones.length) {
    tables = zones.flatMap((zone) => {
      const nested = extractArray(zone, ["mesas", "tables"]);

      return nested.map((table) => ({
        ...table,
        zona_id: first(table?.zona_id, zone?.id, zone?.zona_id),
        zona_nombre: first(table?.zona_nombre, zone?.nombre, zone?.zona_nombre),
      }));
    });
  }

  return tables;
}

function tableStatus(table) {
  const pedido =
    table?.pedido ||
    table?.pedido_activo ||
    table?.pedidoActual ||
    table?.pedido_actual ||
    {};

  return normalizeStatus(
    first(
      table?.estado_operativo,
      table?.estado_mesa,
      table?.estado,
      pedido?.estado_mesa,
      pedido?.estado
    )
  );
}

function buildSalonIndicators(payload) {
  const tables = extractTables(payload).filter((table) => table?.activo !== false);
  const statuses = tables.map(tableStatus);

  const count = (status) =>
    statuses.filter((value) => value === status).length;

  const disponibles = count("DISPONIBLE");
  const reservadas = count("RESERVADA");
  const esperando = count("ESPERANDO_PEDIDO");
  const abiertas = count("ABIERTA");
  const cocina = count("EN_COCINA");
  const listas = count("LISTA");
  const cuentas = count("CUENTA_SOLICITADA");

  const activas = statuses.filter(
    (status) =>
      status &&
      !["DISPONIBLE", "RESERVADA", "FUERA_SERVICIO"].includes(status)
  ).length;

  return {
    salon: {
      count: disponibles,
      label: `${disponibles} disponible${disponibles === 1 ? "" : "s"}`,
      tone: "success",
      showZero: true,
    },

    mesero: {
      count: listas || esperando,
      label: listas
        ? `${listas} lista${listas === 1 ? "" : "s"}`
        : esperando
          ? `${esperando} por atender`
          : activas
            ? `${activas} mesa${activas === 1 ? "" : "s"} activa${activas === 1 ? "" : "s"}`
            : "Sin pendientes",
      tone: listas ? "success" : esperando ? "warning" : "neutral",
      showZero: false,
    },

    cocina: {
      count: listas || cocina,
      label: listas
        ? `${listas} lista${listas === 1 ? "" : "s"}`
        : cocina
          ? `${cocina} en preparación`
          : "Sin comandas",
      tone: listas ? "success" : cocina ? "warning" : "neutral",
      showZero: false,
    },

    caja: {
      count: cuentas,
      label: cuentas
        ? `${cuentas} cuenta${cuentas === 1 ? "" : "s"} pendiente${cuentas === 1 ? "" : "s"}`
        : "Sin cuentas pendientes",
      tone: cuentas ? "danger" : "neutral",
      showZero: false,
    },

    anfitrion: {
      count: reservadas || disponibles,
      label: reservadas
        ? `${reservadas} reserva${reservadas === 1 ? "" : "s"}`
        : `${disponibles} libre${disponibles === 1 ? "" : "s"}`,
      tone: reservadas ? "info" : "success",
      showZero: false,
    },

    _meta: {
      disponibles,
      reservadas,
      esperando,
      abiertas,
      cocina,
      listas,
      cuentas,
      activas,
    },
  };
}

function normalizeIndicator(value, fallback) {
  if (value === undefined || value === null) return fallback;

  if (typeof value === "number") {
    return {
      ...fallback,
      count: value,
    };
  }

  if (typeof value === "object") {
    return {
      ...fallback,
      ...value,
      count: Number.isFinite(Number(value.count))
        ? Number(value.count)
        : Number(fallback?.count || 0),
    };
  }

  return fallback;
}

function toneStyle(tone) {
  switch (tone) {
    case "danger":
      return {
        color: COLORS.red,
        background: COLORS.redSoft,
        dot: COLORS.red,
      };

    case "warning":
      return {
        color: COLORS.amber,
        background: COLORS.amberSoft,
        dot: COLORS.amber,
      };

    case "info":
      return {
        color: COLORS.blue,
        background: COLORS.blueSoft,
        dot: COLORS.blue,
      };

    case "purple":
      return {
        color: COLORS.purple,
        background: COLORS.purpleSoft,
        dot: COLORS.purple,
      };

    case "success":
      return {
        color: COLORS.green,
        background: COLORS.greenSoft,
        dot: COLORS.green,
      };

    default:
      return {
        color: COLORS.muted,
        background: "#f1f4f2",
        dot: "#9aa59f",
      };
  }
}

function routeIsActive(pathname, route) {
  if (route === "/salon") {
    return (
      pathname === "/" ||
      pathname === "/restaurant" ||
      pathname.startsWith("/salon")
    );
  }

  return pathname.startsWith(route);
}

export default function RestaurantNavbar({
  indicators: indicatorsProp = {},
  logoSrc = LOGO_SRC,
  brandLabel = "Tavolata",
  userName = "",
  userRole = "",
  onNavigate,
  onLogout,
  refreshMs = REFRESH_MS,
}) {
  const mountedRef = useRef(true);

  const [pathname, setPathname] = useState(
    () => window.location.pathname || "/salon"
  );

  const [salonIndicators, setSalonIndicators] = useState({});
  const [runtimeIndicators, setRuntimeIndicators] = useState({});
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const loadIndicators = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);

      try {
        const response = await api.get("/restaurante/salon");

        if (!mountedRef.current) return;

        setSalonIndicators(buildSalonIndicators(response.data));
        setLastUpdate(new Date());
        setOnline(true);
      } catch (error) {
        if (mountedRef.current && !navigator.onLine) {
          setOnline(false);
        }
      } finally {
        if (mountedRef.current && !silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    mountedRef.current = true;
    loadIndicators();

    return () => {
      mountedRef.current = false;
    };
  }, [loadIndicators]);

  useEffect(() => {
    if (!refreshMs || refreshMs < 3000) return undefined;

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadIndicators({ silent: true });
      }
    }, refreshMs);

    return () => window.clearInterval(interval);
  }, [loadIndicators, refreshMs]);

  useEffect(() => {
    const syncRoute = () => {
      setPathname(window.location.pathname || "/salon");
    };

    const setConnected = () => {
      setOnline(true);
      loadIndicators({ silent: true });
    };

    const setDisconnected = () => {
      setOnline(false);
    };

    const receiveIndicators = (event) => {
      const detail = event?.detail;

      if (!detail || typeof detail !== "object") return;

      setRuntimeIndicators((current) => ({
        ...current,
        ...detail,
      }));

      setLastUpdate(new Date());
    };

    const forceRefresh = () => {
      loadIndicators({ silent: true });
    };

    window.addEventListener("popstate", syncRoute);
    window.addEventListener("online", setConnected);
    window.addEventListener("offline", setDisconnected);
    window.addEventListener("tavolata:indicators", receiveIndicators);
    window.addEventListener("tavolata:notifications-refresh", forceRefresh);

    return () => {
      window.removeEventListener("popstate", syncRoute);
      window.removeEventListener("online", setConnected);
      window.removeEventListener("offline", setDisconnected);
      window.removeEventListener("tavolata:indicators", receiveIndicators);
      window.removeEventListener("tavolata:notifications-refresh", forceRefresh);
    };
  }, [loadIndicators]);

  const indicators = useMemo(() => {
    const keys = ["salon", "mesero", "cocina", "caja", "anfitrion"];
    const merged = {};

    keys.forEach((key) => {
      let value =
        salonIndicators?.[key] || {
          count: 0,
          label: "",
          tone: "neutral",
          showZero: false,
        };

      value = normalizeIndicator(runtimeIndicators?.[key], value);
      value = normalizeIndicator(indicatorsProp?.[key], value);

      merged[key] = value;
    });

    return merged;
  }, [indicatorsProp, runtimeIndicators, salonIndicators]);

  const navItems = useMemo(
    () => [
      {
        key: "salon",
        label: "Salón",
        route: "/salon",
        icon: "salon",
      },
      {
        key: "mesero",
        label: "Mesero",
        route: "/mesero",
        icon: "mesero",
      },
      {
        key: "cocina",
        label: "Cocina",
        route: "/cocina",
        icon: "cocina",
      },
      {
        key: "caja",
        label: "Caja",
        route: "/caja",
        icon: "caja",
      },
      {
        key: "anfitrion",
        label: "Anfitrión",
        route: "/anfitrion",
        icon: "anfitrion",
      },
    ],
    []
  );

  function navigate(route, event) {
    event?.preventDefault?.();

    if (onNavigate) {
      onNavigate(route);
    } else {
      window.history.pushState({}, "", route);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }

    setPathname(route);
  }

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null") || {};
    } catch (_) {
      return {};
    }
  }, []);

  const displayUserName = String(
    userName ||
      storedUser?.nombre ||
      storedUser?.name ||
      storedUser?.nombres ||
      storedUser?.username ||
      storedUser?.email ||
      ""
  ).trim();

  const performLogout = useCallback(() => {
    setLogoutOpen(false);

    if (typeof onLogout === "function") {
      onLogout();
      return;
    }

    localStorage.removeItem("token");
    localStorage.removeItem("tenantid");
    localStorage.removeItem("tenant");
    localStorage.removeItem("user");

    try {
      if (api?.defaults?.headers?.common) {
        delete api.defaults.headers.common.Authorization;
        delete api.defaults.headers.common.authorization;
        delete api.defaults.headers.common.tenantid;
        delete api.defaults.headers.common["tenant-id"];
      }
    } catch (_) {
      // La redirección a /login termina de limpiar el estado de la app.
    }

    window.location.assign("/login");
  }, [onLogout]);

  const initials = String(displayUserName || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const updateText = lastUpdate
    ? lastUpdate.toLocaleTimeString("es-CL", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <>
      <style>{`
        :root {
          --tavolata-navbar-height: 82px;
        }

        .tav-nav,
        .tav-nav * {
          box-sizing: border-box;
        }

        /*
         * IMPORTANTE:
         * RestaurantShell ya deja el espacio para el navbar.
         * Por eso aquí debe ser FIXED y no STICKY.
         */
        .tav-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1400;
          width: 100%;
          height: var(--tavolata-navbar-height);
          background: rgba(255, 255, 255, .97);
          border-bottom: 1px solid ${COLORS.line};
          box-shadow: 0 4px 18px rgba(20, 35, 28, .055);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        .tav-nav__inner {
          width: min(1600px, 100%);
          height: 100%;
          margin: 0 auto;
          padding: 8px 18px;
          display: grid;
          grid-template-columns: minmax(175px, 230px) minmax(0, 1fr) auto;
          align-items: center;
          gap: 16px;
        }

        .tav-nav__brand {
          min-width: 0;
          height: 56px;
          display: flex;
          align-items: center;
          text-decoration: none;
          color: ${COLORS.ink};
          border-radius: 14px;
          padding: 3px 6px;
          transition:
            background .16s ease,
            transform .16s ease;
        }

        .tav-nav__brand:hover {
          background: ${COLORS.surfaceSoft};
        }

        .tav-nav__brand:active {
          transform: scale(.99);
        }

        .tav-nav__logo {
          display: block;
          width: auto;
          height: 70px;
          max-width: 280px;
          object-fit: contain;
          object-position: left center;
        }

        .tav-nav__logo-fallback {
          display: none;
          align-items: center;
          gap: 9px;
          color: ${COLORS.ink};
          font-size: 22px;
          font-weight: 900;
          letter-spacing: -.04em;
        }

        .tav-nav__fallback-mark {
          width: 36px;
          height: 36px;
          border-radius: 11px;
          display: grid;
          place-items: center;
          color: #fff;
          background: linear-gradient(135deg, #f2ae51, #dc7c22);
          box-shadow: 0 7px 16px rgba(226, 137, 38, .2);
        }

        .tav-nav__menu-wrap {
          min-width: 0;
          overflow: hidden;
        }

        .tav-nav__menu {
          min-width: max-content;
          display: flex;
          align-items: stretch;
          justify-content: center;
          gap: 5px;
        }

        .tav-nav__item {
          position: relative;
          min-width: 106px;
          height: 58px;
          padding: 7px 11px;
          border: 0;
          border-radius: 15px;
          background: transparent;
          color: ${COLORS.muted};
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          cursor: pointer;
          transition:
            background .16s ease,
            color .16s ease,
            box-shadow .16s ease,
            transform .16s ease;
          -webkit-tap-highlight-color: transparent;
        }

        .tav-nav__item:hover {
          color: ${COLORS.ink};
          background: #f4f7f5;
        }

        .tav-nav__item:active {
          transform: scale(.98);
        }

        .tav-nav__item.is-active {
          color: ${COLORS.ink};
          background: ${COLORS.brandSoft};
          box-shadow:
            inset 0 0 0 1px rgba(230, 141, 47, .24),
            0 3px 10px rgba(230, 141, 47, .07);
        }

        .tav-nav__item.is-active::after {
          content: "";
          position: absolute;
          left: 17px;
          right: 17px;
          bottom: -12px;
          height: 3px;
          border-radius: 4px 4px 0 0;
          background: linear-gradient(
            90deg,
            #f2ad4e,
            ${COLORS.brand}
          );
        }

        .tav-nav__icon-wrap {
          position: relative;
          flex: 0 0 auto;
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
        }

        .tav-nav__mui-icon {
          font-size: 21px !important;
          color: currentColor;
        }

        .tav-nav__wifi-icon {
          font-size: 16px !important;
          color: currentColor;
        }

        .tav-nav__refresh-icon {
          font-size: 19px !important;
          color: currentColor;
        }

        .tav-nav__badge {
          position: absolute;
          top: -8px;
          right: -10px;
          min-width: 19px;
          height: 19px;
          padding: 0 5px;
          border: 2px solid #fff;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          line-height: 1;
          font-weight: 900;
          letter-spacing: -.02em;
          box-shadow: 0 3px 8px rgba(15, 23, 42, .13);
        }

        .tav-nav__copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          line-height: 1.05;
        }

        .tav-nav__label {
          color: inherit;
          font-size: 13px;
          font-weight: 850;
          white-space: nowrap;
        }

        .tav-nav__indicator {
          position: relative;
          max-width: 105px;
          margin-top: 5px;
          padding-left: 9px;
          color: #7c8881;
          font-size: 10px;
          font-weight: 750;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .tav-nav__indicator::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          transform: translateY(-50%);
          background: var(--indicator-dot, #9aa59f);
        }

        .tav-nav__item.is-active .tav-nav__indicator {
          color: #8a5a20;
        }

        .tav-nav__right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
        }

        .tav-nav__connection {
          height: 38px;
          padding: 0 10px;
          border: 1px solid ${COLORS.line};
          border-radius: 12px;
          background: #fff;
          color: ${COLORS.muted};
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 11px;
          font-weight: 760;
          white-space: nowrap;
        }

        .tav-nav__connection-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${COLORS.green};
          box-shadow: 0 0 0 3px ${COLORS.greenSoft};
        }

        .tav-nav__connection.is-offline .tav-nav__connection-dot {
          background: ${COLORS.red};
          box-shadow: 0 0 0 3px ${COLORS.redSoft};
        }

        .tav-nav__refresh {
          width: 38px;
          height: 38px;
          flex: 0 0 38px;
          padding: 0;
          border: 1px solid ${COLORS.line};
          border-radius: 12px;
          background: #fff;
          color: ${COLORS.muted};
          cursor: pointer;
          display: grid;
          place-items: center;
          transition:
            background .16s ease,
            color .16s ease,
            transform .16s ease;
        }

        .tav-nav__refresh:hover:not(:disabled) {
          color: ${COLORS.ink};
          background: ${COLORS.surfaceSoft};
        }

        .tav-nav__refresh:active:not(:disabled) {
          transform: scale(.96);
        }

        .tav-nav__refresh:disabled {
          cursor: default;
        }

        .tav-nav__refresh.is-loading svg {
          animation: tav-spin .75s linear infinite;
        }

        .tav-nav__logout {
          min-width: 40px;
          height: 40px;
          padding: 0 11px;
          border: 1px solid #fecaca;
          border-radius: 12px;
          background: #fff7f7;
          color: #c62828;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          font: inherit;
          font-size: 11px;
          font-weight: 850;
          white-space: nowrap;
          transition:
            background .16s ease,
            border-color .16s ease,
            color .16s ease,
            transform .16s ease;
        }

        .tav-nav__logout:hover {
          background: #fff0f0;
          border-color: #fca5a5;
          color: #b91c1c;
        }

        .tav-nav__logout:active {
          transform: scale(.96);
        }

        .tav-nav__logout-icon {
          font-size: 19px !important;
        }

        .tav-logout-modal {
          position: fixed;
          inset: 0;
          z-index: 2200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15, 23, 42, .46);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }

        .tav-logout-card {
          width: min(420px, calc(100vw - 32px));
          overflow: hidden;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          background: #fff;
          box-shadow: 0 24px 70px rgba(15, 23, 42, .24);
        }

        .tav-logout-head {
          padding: 18px 20px 14px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          border-bottom: 1px solid #eef1ef;
        }

        .tav-logout-title {
          margin: 0;
          color: ${COLORS.ink};
          font-size: 18px;
          font-weight: 900;
          letter-spacing: -.02em;
        }

        .tav-logout-subtitle {
          margin: 5px 0 0;
          color: #748078;
          font-size: 12px;
          line-height: 1.45;
        }

        .tav-logout-close {
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          padding: 0;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #fff;
          color: #64748b;
          cursor: pointer;
          display: grid;
          place-items: center;
        }

        .tav-logout-close .MuiSvgIcon-root {
          font-size: 18px;
        }

        .tav-logout-body {
          padding: 18px 20px;
        }

        .tav-logout-user {
          padding: 12px 14px;
          border: 1px solid #e7ebe8;
          border-radius: 12px;
          background: #f8faf9;
        }

        .tav-logout-user span {
          display: block;
          color: #7b877f;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .05em;
        }

        .tav-logout-user strong {
          display: block;
          margin-top: 4px;
          color: ${COLORS.ink};
          font-size: 14px;
          font-weight: 850;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tav-logout-actions {
          padding: 0 20px 20px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .tav-logout-action {
          min-height: 40px;
          padding: 0 15px;
          border-radius: 11px;
          border: 1px solid #dfe4e1;
          background: #fff;
          color: #425148;
          cursor: pointer;
          font: inherit;
          font-size: 12px;
          font-weight: 850;
        }

        .tav-logout-action.danger {
          border-color: #dc2626;
          background: #dc2626;
          color: #fff;
        }

        .tav-logout-action.danger:hover {
          background: #b91c1c;
          border-color: #b91c1c;
        }

        .tav-nav__user {
          min-width: 40px;
          height: 40px;
          padding: 0 8px 0 5px;
          border: 1px solid ${COLORS.line};
          border-radius: 13px;
          background: #f7f9f8;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tav-nav__avatar {
          width: 30px;
          height: 30px;
          flex: 0 0 30px;
          border-radius: 10px;
          background: #203b30;
          color: #fff;
          display: grid;
          place-items: center;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: .02em;
        }

        .tav-nav__user-copy {
          min-width: 0;
          max-width: 130px;
          line-height: 1.05;
        }

        .tav-nav__user-name,
        .tav-nav__user-role {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .tav-nav__user-name {
          color: ${COLORS.ink};
          font-size: 11px;
          font-weight: 850;
        }

        .tav-nav__user-role {
          margin-top: 3px;
          color: #87928c;
          font-size: 9px;
          font-weight: 700;
        }

        @keyframes tav-spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 1180px) {
          .tav-nav__inner {
            grid-template-columns: 165px minmax(0, 1fr) auto;
            gap: 9px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .tav-nav__logo {
            max-width: 158px;
            height: 40px;
          }

          .tav-nav__item {
            min-width: 88px;
            padding-left: 8px;
            padding-right: 8px;
          }

          .tav-nav__indicator {
            max-width: 75px;
          }

          .tav-nav__connection {
            width: 38px;
            padding: 0;
            justify-content: center;
          }

          .tav-nav__connection > span:last-child {
            display: none;
          }

          .tav-nav__user-copy {
            display: none;
          }

          .tav-nav__user {
            padding-right: 5px;
          }

          .tav-nav__logout {
            width: 40px;
            padding: 0;
          }

          .tav-nav__logout-label {
            display: none;
          }
        }

        @media (max-width: 900px) {
          :root {
            --tavolata-navbar-height: 76px;
          }

          .tav-nav__inner {
            grid-template-columns: 135px minmax(0, 1fr) auto;
            padding: 7px 9px;
            gap: 7px;
          }

          .tav-nav__brand {
            height: 50px;
          }

          .tav-nav__logo {
            height: 34px;
            max-width: 130px;
          }

          .tav-nav__menu-wrap {
            overflow-x: auto;
            overscroll-behavior-x: contain;
            scrollbar-width: none;
          }

          .tav-nav__menu-wrap::-webkit-scrollbar {
            display: none;
          }

          .tav-nav__menu {
            width: max-content;
            justify-content: flex-start;
          }

          .tav-nav__item {
            min-width: 91px;
            height: 54px;
            gap: 6px;
            padding: 6px 7px;
          }

          .tav-nav__item.is-active::after {
            bottom: -11px;
          }

          .tav-nav__icon-wrap {
            width: 24px;
            height: 24px;
          }

          .tav-nav__mui-icon {
            font-size: 20px !important;
          }

          .tav-nav__label {
            font-size: 12px;
          }

          .tav-nav__indicator {
            max-width: 65px;
            font-size: 9px;
          }

          .tav-nav__connection {
            display: none;
          }
        }

        @media (max-width: 620px) {
          :root {
            --tavolata-navbar-height: 70px;
          }

          .tav-nav__inner {
            grid-template-columns: 96px minmax(0, 1fr) auto;
            padding: 6px 7px;
            gap: 5px;
          }

          .tav-nav__logo {
            height: 29px;
            max-width: 92px;
          }

          .tav-nav__item {
            min-width: 79px;
            height: 50px;
            gap: 5px;
            padding: 5px 6px;
          }

          .tav-nav__copy {
            align-items: center;
          }

          .tav-nav__indicator {
            display: none;
          }

          .tav-nav__badge {
            top: -7px;
            right: -8px;
          }

          .tav-nav__user {
            display: none;
          }

          .tav-nav__refresh {
            display: none;
          }

          .tav-nav__logout {
            width: 36px;
            min-width: 36px;
            height: 36px;
            padding: 0;
          }
        }
      `}</style>

      <header className="tav-nav">
        <div className="tav-nav__inner">
          <a
            href="/salon"
            className="tav-nav__brand"
            onClick={(event) => navigate("/salon", event)}
            aria-label="Ir al Salón de Tavolata"
          >
            <img
              src={logoSrc}
              alt={`${brandLabel} by IntegraNube`}
              className="tav-nav__logo"
              onError={(event) => {
                event.currentTarget.style.display = "none";
                const fallback = event.currentTarget.nextElementSibling;

                if (fallback) {
                  fallback.style.display = "flex";
                }
              }}
            />

            <span className="tav-nav__logo-fallback">
              <span className="tav-nav__fallback-mark">T</span>
              {brandLabel}
            </span>
          </a>

          <div className="tav-nav__menu-wrap">
            <nav
              className="tav-nav__menu"
              aria-label="Navegación de restaurante"
            >
              {navItems.map((item) => {
                const active = routeIsActive(pathname, item.route);
                const indicator = indicators[item.key] || {};
                const tone = toneStyle(indicator.tone);
                const count = Number(indicator.count || 0);
                const showBadge =
                  count > 0 || indicator.showZero === true;
                const NavIcon = NAV_ICONS[item.icon] || SalonIcon;

                return (
                  <a
                    key={item.key}
                    href={item.route}
                    className={`tav-nav__item${active ? " is-active" : ""}`}
                    onClick={(event) => navigate(item.route, event)}
                    aria-current={active ? "page" : undefined}
                    title={indicator.label || item.label}
                  >
                    <span className="tav-nav__icon-wrap">
                      <NavIcon className="tav-nav__mui-icon" aria-hidden="true" />

                      {showBadge && (
                        <span
                          className="tav-nav__badge"
                          style={{
                            color: tone.color,
                            background: tone.background,
                          }}
                          aria-label={`${count} notificaciones`}
                        >
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </span>

                    <span className="tav-nav__copy">
                      <span className="tav-nav__label">
                        {item.label}
                      </span>

                      <span
                        className="tav-nav__indicator"
                        style={{
                          "--indicator-dot": tone.dot,
                        }}
                      >
                        {indicator.label || "Sin pendientes"}
                      </span>
                    </span>
                  </a>
                );
              })}
            </nav>
          </div>

          <div className="tav-nav__right">
            <div
              className={`tav-nav__connection${
                online ? "" : " is-offline"
              }`}
              title={
                online
                  ? `Conectado${
                      updateText
                        ? ` · actualizado ${updateText}`
                        : ""
                    }`
                  : "Sin conexión"
              }
            >
              <span className="tav-nav__connection-dot" />
              <WifiIcon className="tav-nav__wifi-icon" aria-hidden="true" />
              <span>{online ? "Online" : "Offline"}</span>
            </div>

            <button
              type="button"
              className={`tav-nav__refresh${
                loading ? " is-loading" : ""
              }`}
              onClick={() => loadIndicators()}
              disabled={loading}
              title={
                updateText
                  ? `Actualizar · último estado ${updateText}`
                  : "Actualizar indicadores"
              }
              aria-label="Actualizar indicadores"
            >
              <RefreshIcon className="tav-nav__refresh-icon" aria-hidden="true" />
            </button>

            {(displayUserName || userRole) && (
              <div
                className="tav-nav__user"
                title={[displayUserName, userRole].filter(Boolean).join(" · ")}
              >
                <span className="tav-nav__avatar">
                  {initials || "TV"}
                </span>

                <span className="tav-nav__user-copy">
                  {displayUserName && (
                    <span className="tav-nav__user-name">
                      {displayUserName}
                    </span>
                  )}

                  {userRole && (
                    <span className="tav-nav__user-role">
                      {userRole}
                    </span>
                  )}
                </span>
              </div>
            )}

            <button
              type="button"
              className="tav-nav__logout"
              onClick={() => setLogoutOpen(true)}
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
            >
              <LogoutIcon className="tav-nav__logout-icon" aria-hidden="true" />
              <span className="tav-nav__logout-label">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {logoutOpen && (
        <div
          className="tav-logout-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setLogoutOpen(false);
            }
          }}
        >
          <div
            className="tav-logout-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tav-logout-title"
          >
            <div className="tav-logout-head">
              <div>
                <h2 id="tav-logout-title" className="tav-logout-title">
                  Cerrar sesión
                </h2>
                <p className="tav-logout-subtitle">
                  ¿Deseas desconectarte de Tavolata?
                </p>
              </div>

              <button
                type="button"
                className="tav-logout-close"
                onClick={() => setLogoutOpen(false)}
                aria-label="Cerrar"
              >
                <CloseIcon aria-hidden="true" />
              </button>
            </div>

            <div className="tav-logout-body">
              <div className="tav-logout-user">
                <span>Sesión actual</span>
                <strong>{displayUserName || "Usuario conectado"}</strong>
              </div>
            </div>

            <div className="tav-logout-actions">
              <button
                type="button"
                className="tav-logout-action"
                onClick={() => setLogoutOpen(false)}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="tav-logout-action danger"
                onClick={performLogout}
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
